// Format date for display
export const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

// Format date as dd/mm/yyyy for compact display (e.g. MOT history rows)
export const formatDateShort = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

// Format a date-only `YYYY-MM-DD` string as dd/mm/yyyy without constructing a
// timezone-sensitive `Date`. A bare `new Date('2026-08-01')` parses as UTC
// midnight, so `toLocaleDateString` in a timezone west of UTC renders the
// previous day (e.g. 2026-08-01 -> 31/07/2026). Parsing the parts explicitly
// keeps the displayed date stable and identical to the source value regardless
// of the viewer's timezone. Malformed or empty input is returned as-is rather
// than throwing, so callers never need a defensive wrapper.
export const formatDateOnlyShort = (dateString: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString?.trim() ?? '');
    if (!match) {
        return dateString ?? '';
    }
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
};

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
] as const;

// Same no-`Date` rule as formatDateOnlyShort: the month is read from the string, never a timezone.
export const formatDateOnlyMonthYear = (dateString: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString?.trim() ?? '');
    const monthName = match ? MONTH_NAMES[Number(match[2]) - 1] : undefined;
    if (!match || monthName === undefined) {
        return dateString ?? '';
    }
    return `${monthName} ${match[1]}`;
};

// Timestamp strings are formatted by their leading YYYY-MM-DD and never by constructing
// a Date from the value: `new Date('2026-09-05')` is UTC midnight, 4 September west of UTC.

// Is `value` a strictly valid `YYYY-MM-DD` CALENDAR date, not merely a value
// of that shape? `2026-99-99`, `2026-02-30` and `2026-08-00` all satisfy
// /^\d{4}-\d{2}-\d{2}$/ and none of them is a date, so the shape regex alone
// would let a corrupt prefix through and print `99/99/2026`.
//
// This DUPLICATES `features/quote/utils/dateKey.ts::isDateKey` (#767) -- same
// regex, same range guards, same days-in-month lookup, so the same answers,
// including that lookup's inherited `Date.UTC` mapping of years 0-99 onto
// 1900-1999. The duplication is deliberate, not an oversight: every
// `app/utils` -> `features` import in this codebase today is an `import type`
// (`vehicleScheduleContext.ts`), which the compiler erases. A RUNTIME import
// from here would give every consumer of this module -- mot-history, quote and
// dashboard alike -- a transitive dependency on the quote feature, a broader
// version of the coupling the note above says this file exists to avoid. The
// structural fix is to promote the predicate into `app/utils` and have
// `dateKey.ts` re-export it, which edits a file this slice must not touch.
export const isCalendarDateKey = (value: string): boolean => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1) {
        return false;
    }
    // `Date.UTC(y, m, 0)` is the last day of the 1-based month `m`, used PURELY
    // as a days-in-month lookup. Nothing is formatted from this Date, so the
    // no-timezone-exposure invariant above is preserved.
    return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
};

export const formatIsoDateOnlyShort = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }
    // The leading-`YYYY-MM-DD` prefix rule (the same one as
    // `dateKey.ts::toDateKey`) applies only when that prefix is a real date.
    // Anything else is passed through WHOLE for `formatDateOnlyShort` to return
    // unchanged, so a bad payload is visible rather than trimmed into shape.
    const prefix = value.slice(0, 10);
    return formatDateOnlyShort(isCalendarDateKey(prefix) ? prefix : value);
};

// The long form of formatIsoDateOnlyShort, by the same prefix rule: `2026-10-19T…` -> `19 October 2026`.
export const formatIsoDateLong = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }
    const prefix = value.slice(0, 10);
    if (!isCalendarDateKey(prefix)) {
        return value;
    }
    const [year, month, day] = prefix.split('-');
    return `${Number(day)} ${MONTH_NAMES[Number(month) - 1]} ${year}`;
};
